import React from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { formatValue } from "@/lib/api";
import { Loader2, ExternalLink, Copy, Flame, Sparkles, Star, Trash2 } from "lucide-react";
import DmeTag from "@/components/DmeTag";
import { POS_COLORS } from "./shared";

export default function TradeDeskTab({ tradeDesk, watch, pitches, pitchLoading, copyBlueprint, writePitch, toggleWatch, isWatched }) {
  return (
    <div data-testid="ptab-content-tradedesk">
      <p className="text-sm text-[var(--text-2)] mb-4">
        Buy-low targets across all your leagues — movable depth & pick-burner picks priced below our valuation, each with a ready-to-send offer.
        <span className="text-[var(--text-3)]"> Live rosters, scored continuously.</span>
      </p>
      {(watch || []).length > 0 && (
        <div className="card-bordered p-4 mb-4" data-testid="td-watchlist">
          <div className="flex items-center gap-2 mb-3 text-[10px] tracking-[0.2em] text-[var(--amber)] font-mono">
            <Star className="w-3.5 h-3.5 fill-[var(--amber)]" /> WATCHLIST · {watch.length}
            <span className="text-[var(--text-3)] normal-case tracking-normal font-sans">— we'll alert you on buy-low windows & owner desperation</span>
          </div>
          <div className="space-y-1.5">
            {watch.map((w, wi) => (
              <div key={`${w.league_id}-${w.player_id}`} className="py-1.5 border-b border-[var(--border)] last:border-0" data-testid={`td-watch-row-${wi}`}>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <span className="font-medium">{w.name}</span>
                    <span className="text-[10px] tracking-widest text-[var(--text-3)] ml-2">{w.league_name} · {w.owner_name}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {typeof w.value_change_pct === "number" && w.value_change_pct !== 0 && (
                      <span className={`text-xs font-mono ${w.value_change_pct < 0 ? "text-[var(--accent)]" : "text-[var(--text-3)]"}`}>{w.value_change_pct > 0 ? "+" : ""}{w.value_change_pct}%</span>
                    )}
                    {w.current_desperation && w.current_desperation !== "Stable" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--amber)]/15 text-[var(--amber)]">{w.current_desperation}</span>
                    )}
                    <span className="font-mono text-[#60A5FA]">{formatValue(w.current_value || w.base_value)}</span>
                    <button onClick={() => toggleWatch(w)} className="text-[var(--text-3)] hover:text-[var(--amber)]" data-testid={`td-watch-remove-${wi}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {w.why_now && (
                  <div className="mt-1.5 flex items-start gap-1.5 text-xs text-[var(--amber)]" data-testid={`td-why-now-${wi}`}>
                    <Sparkles className="w-3 h-3 mt-0.5 shrink-0" /><span>{w.why_now}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {tradeDesk === null ? (
        <div className="card-bordered p-10 flex flex-col items-center gap-3">
          <Loader2 className="w-5 h-5 spin text-[var(--accent)]" />
          <div className="text-xs text-[var(--text-3)]">Scanning every roster + trade history across your leagues…</div>
        </div>
      ) : (tradeDesk.targets || []).length === 0 ? (
        <div className="card-bordered p-8 text-center text-sm text-[var(--text-3)]" data-testid="tradedesk-empty">
          No clear buy-low targets right now across {tradeDesk.leagues_scanned || 0} league{(tradeDesk.leagues_scanned || 0) !== 1 ? "s" : ""}. We'll surface depth pieces and pick-burner picks the moment they appear.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {tradeDesk.targets.map((t, i) => {
            const so = t.suggested_offer;
            return (
              <div key={`${t.league_id}-${t.player_id}-${i}`} className="card-bordered p-4 flex flex-col gap-3" data-testid={`td-target-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {t.kind === "pick" ? <Flame className="w-3.5 h-3.5 text-[#60A5FA] shrink-0" /> : null}
                      {t.kind === "player" && t.player_id ? (
                        <Link to={`/app/players/${t.player_id}`} className="font-display font-bold hover:text-[var(--accent)] truncate" data-testid={`td-name-${i}`}>{t.name}</Link>
                      ) : (
                        <span className="font-display font-bold truncate" data-testid={`td-name-${i}`}>{t.name}</span>
                      )}
                    </div>
                    <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono mt-0.5">
                      <span style={{ color: POS_COLORS[t.position] || "var(--text-3)" }}>{t.position}</span> · {t.league_name} · {t.owner_name}
                    </div>
                  </div>
                  <div className="text-right shrink-0 flex items-start gap-2">
                    <button onClick={() => toggleWatch(t)} title={isWatched(t) ? "Watching" : "Watch for buy-low window"} className="mt-0.5 text-[var(--text-3)] hover:text-[var(--amber)] transition-colors" data-testid={`td-watch-${i}`}>
                      <Star className={`w-4 h-4 ${isWatched(t) ? "fill-[var(--amber)] text-[var(--amber)]" : ""}`} />
                    </button>
                    <div>
                      <div className="font-mono text-lg" style={{ color: t.kind === "pick" ? "#60A5FA" : "var(--accent)" }}>{formatValue(t.value)}</div>
                      <div className="text-[9px] tracking-widest text-[var(--text-3)] flex items-center gap-1 justify-end">VALUE <DmeTag /></div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-[10px] tracking-widest text-[var(--text-3)] font-mono mb-1">
                    <span>ACQUIRABILITY</span><span data-testid={`td-acq-${i}`}>{t.acquirability}%</span>
                  </div>
                  <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${t.acquirability}%`, background: t.acquirability >= 60 ? "var(--accent)" : t.acquirability >= 40 ? "#60A5FA" : "var(--amber)" }} />
                  </div>
                </div>

                <ul className="space-y-1">
                  {t.signals.map((s, si) => (
                    <li key={`${t.player_id}-sig-${si}`} className="text-xs text-[var(--text-2)] flex items-start gap-1.5">
                      <span className="text-[var(--accent)] mt-0.5">›</span><span>{s}</span>
                    </li>
                  ))}
                </ul>

                {so && (
                  <div className="rounded-sm bg-black/30 border border-[var(--border)] p-3" data-testid={`td-offer-${i}`}>
                    <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono mb-2">SUGGESTED OFFER · Δ{so.delta_pct > 0 ? "+" : ""}{so.delta_pct}%</div>
                    <div className="flex flex-wrap items-center gap-1.5 text-xs">
                      <span className="text-[var(--text-3)]">You send</span>
                      {so.you_give.map((g, gi) => (
                        <span key={`${g.name}-${gi}`} className="px-2 py-0.5 rounded-full bg-white/5 border border-[var(--border)]">{g.name} <span className="text-[var(--text-3)] font-mono">{formatValue(g.value)}</span></span>
                      ))}
                      <span className="text-[var(--accent)]">→</span>
                      <span className="px-2 py-0.5 rounded-full bg-[var(--accent)]/10 border border-[var(--accent)]/30">{t.name}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <button onClick={() => copyBlueprint(so.text)} className="btn-secondary text-xs inline-flex items-center gap-1.5" data-testid={`td-copy-${i}`}>
                        <Copy className="w-3.5 h-3.5" /> Copy blueprint
                      </button>
                      <button onClick={() => writePitch(i, t)} disabled={pitchLoading[i]} className="btn-secondary text-xs inline-flex items-center gap-1.5" data-testid={`td-pitch-${i}`}>
                        {pitchLoading[i] ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Sparkles className="w-3.5 h-3.5" />} Write AI pitch
                      </button>
                      <a href={`https://sleeper.com/leagues/${t.league_id}`} target="_blank" rel="noreferrer" className="text-xs text-[var(--accent)] hover:underline inline-flex items-center gap-1" data-testid={`td-open-${i}`}>
                        Open league <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    {pitches[i] && (
                      <div className="mt-3 pt-3 border-t border-[var(--border)]" data-testid={`td-pitch-result-${i}`}>
                        <div className="text-[10px] tracking-widest text-[var(--accent)] font-mono mb-2 inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> AI PITCH · tailored to {t.owner_name}</div>
                        <div className="md-body text-sm"><ReactMarkdown remarkPlugins={[remarkGfm]}>{pitches[i]}</ReactMarkdown></div>
                        <button onClick={() => copyBlueprint(pitches[i])} className="btn-secondary text-xs inline-flex items-center gap-1.5 mt-2" data-testid={`td-pitch-copy-${i}`}>
                          <Copy className="w-3.5 h-3.5" /> Copy pitch
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
